import { Component, OnInit, OnDestroy, ViewChild, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { PythonModuleService } from '../../services/python-module.service';
import { takeUntil, Subject } from 'rxjs';
import { FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { UploadComponent, UploadModule, FileRestrictions, UploadEvent, RemoveEvent, SelectEvent } from '@progress/kendo-angular-upload';
import { ProgressBarModule } from '@progress/kendo-angular-progressbar';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IconsModule } from '@progress/kendo-angular-icons';
import { HttpEventType } from '@angular/common/http';
import { xIcon, uploadIcon, SVGIcon, fileCsvIcon, fileExcelIcon, fileIcon, filePdfIcon, fileTxtIcon, fileWordIcon, fileZipIcon, filePptIcon, fileReportIcon, dataSqlIcon } from '@progress/kendo-svg-icons';
import * as signalR from '@microsoft/signalr';

@Component({
  selector: 'app-upload-to-staging',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    UploadModule,
    ProgressBarModule,
    ButtonsModule,
    IconsModule
  ],
  templateUrl: './upload-to-staging.component.html',
  styleUrls: ['./upload-to-staging.component.scss']
})
export class UploadToStagingComponent implements OnInit, OnDestroy {
  @ViewChild('upload') upload!: UploadComponent;  route: ActivatedRoute;
  runId: number = 0;
  uploadStates: Map<string, {
    uploading: boolean;
    uploadSuccess: boolean;
    uploadError: boolean;
    uploadProgress: number;
    uploadMessage: string;
    fileName: string;
  }> = new Map();
  
  fileControl = new FormControl('', [Validators.required]);
  uploadRestrictions: FileRestrictions = {
    allowedExtensions: [],
    maxFileSize: 52428800
  };
  myFiles: any[] = [];
  activeUploads: number = 0;
  
  public xIcon: SVGIcon = xIcon;
  public uploadIcon: SVGIcon = uploadIcon;
  public fileExcelIcon: SVGIcon = fileExcelIcon;
  public fileCsvIcon: SVGIcon = fileCsvIcon;
  public fileIcon: SVGIcon = fileIcon;
  public filePdfIcon: SVGIcon = filePdfIcon;
  public filePptIcon: SVGIcon = filePptIcon;
  public fileReportIcon: SVGIcon = fileReportIcon;
  public fileTxtIcon: SVGIcon = fileTxtIcon;
  public fileWordIcon: SVGIcon = fileWordIcon;
  public fileZipIcon: SVGIcon = fileZipIcon;
  public dataSqlIcon: SVGIcon = dataSqlIcon;
  fileExtIconList: any = ['csv', 'doc', 'docx', 'msg', 'pdf', 'pptx', 'txt', 'xls', 'xlsx', 'zip', 'sql'];
  fileExtOther: boolean = false;
  
  private destroy$ = new Subject<void>();
  private hubConnection: signalR.HubConnection | null = null;

  constructor(
    private readonly pythonModuleService: PythonModuleService,
    private readonly renderer: Renderer2,
    route: ActivatedRoute
  ) {
    this.route = route;
  }

  ngOnInit(): void {
    const runIdParam = this.route.snapshot.paramMap.get('runId');
    if (runIdParam) {
      this.runId = parseInt(runIdParam, 10);
      this.initializeSignalR();
    }
  }

  initializeSignalR(): void {
    this.hubConnection = this.pythonModuleService.startConnection(this.runId, false);
    
    if (!this.hubConnection) {
      return;
    }

    this.hubConnection
      .start()
      .then(() => {
        if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
          console.log('SignalR Connection established');
        }
      })
      .catch((err: any) => console.error('SignalR connection error: ', err));

    if (this.hubConnection) {
      this.hubConnection.on('UploadFileProgress', (fileName: string, progress: any) => {
        const uploadState = this.uploadStates.get(fileName);
        if (uploadState) {
          uploadState.uploadProgress = progress;
        }
      });
    }
  }  browseUpload(event: Event): void {
    event.preventDefault();
    if (this.upload) {
      const fileInput = this.upload.wrapper.querySelector('input[type="file"]');
      if (fileInput) {
        (fileInput as HTMLInputElement).click();
      }
    }
  }

  onSelect(event: SelectEvent): void {
    console.log('Files selected:', event.files);
    // This event fires when files are selected, but autoUpload=true should handle the upload
    // Adding this as a debug point and to ensure the component processes the selection
  }

  onRemove(event: RemoveEvent): void {
    const fileToRemove = event.files[0];
    this.myFiles = this.myFiles.filter((file: any) => file.name !== fileToRemove.name);
    this.uploadStates.delete(fileToRemove.name);
    
    if (this.uploadStates.size === 0) {
      this.fileControl.setValue('');
    }
  }
  onUpload(event: UploadEvent): void {
    console.log('onUpload triggered with files:', event.files);
    const files = event.files;
    if (files.length === 0) {
      return;
    }

    if (this.activeUploads === 0) {
      this.renderer.setStyle(document.body, 'cursor', 'wait');
    }

    files.forEach(fileInfo => {
      const file = fileInfo.rawFile;
      if (!file) {
        return;
      }

      const fileName = file.name;
      const fileExtension = fileName.split('.').pop() || '';
      const fileExtOther = !this.fileExtIconList.includes(fileExtension.toLowerCase());

      this.uploadStates.set(fileName, {
        uploading: true,
        uploadSuccess: false,
        uploadError: false,
        uploadProgress: 0,
        uploadMessage: 'Uploading...',
        fileName: fileName
      });

      this.activeUploads++;

      const formData = new FormData();
      formData.append('file', file, fileName);

      this.pythonModuleService.uploadFileToStaging(this.runId, formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (httpEvent: any) => {
            const uploadState = this.uploadStates.get(fileName);
            if (!uploadState) return;

            if (httpEvent.type === HttpEventType.UploadProgress) {
              if (httpEvent.total) {
                uploadState.uploadProgress = Math.round((100 * httpEvent.loaded) / httpEvent.total);
              }
            } else if (httpEvent.type === HttpEventType.Response) {
              uploadState.uploading = false;
              uploadState.uploadSuccess = true;
              uploadState.uploadProgress = 100;
              uploadState.uploadMessage = 'File uploaded successfully to staging!';
              
              this.activeUploads--;
              if (this.activeUploads === 0) {
                this.renderer.setStyle(document.body, 'cursor', 'default');
              }

              const successfulUploads = Array.from(this.uploadStates.values())
                .filter(state => state.uploadSuccess)
                .map(state => state.fileName);
              this.fileControl.setValue(successfulUploads.join(', '));
            }
          },          error: (error) => {
            const uploadState = this.uploadStates.get(fileName);
            if (uploadState) {
              uploadState.uploading = false;
              uploadState.uploadSuccess = false;
              uploadState.uploadError = true;
              uploadState.uploadProgress = 0;
              uploadState.uploadMessage = 'File failed to upload. ' + (error.message || 'Unknown error occurred');
            }

            this.activeUploads--;
            if (this.activeUploads === 0) {
              this.renderer.setStyle(document.body, 'cursor', 'default');
            }
          }
        });
    });
  }
  removeFile(fileName?: string): void {
    if (!fileName) {
      // If no fileName provided, clear all
      this.uploadStates.clear();
      this.myFiles = [];
      this.fileControl.setValue('');
      return;
    }

    const uploadState = this.uploadStates.get(fileName);
    if (!uploadState) return;

    // Only delete from server if the file was successfully uploaded
    if (uploadState.uploadSuccess) {
      this.renderer.setStyle(document.body, 'cursor', 'wait');

      this.pythonModuleService.deleteStagingFile(this.runId, fileName)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            console.log(`Successfully deleted ${fileName} from staging`);
            this.uploadStates.delete(fileName);
            this.myFiles = this.myFiles.filter((file: any) => file.name !== fileName);

            if (this.uploadStates.size === 0) {
              this.fileControl.setValue('');
            } else {
              const successfulUploads = Array.from(this.uploadStates.values())
                .filter(state => state.uploadSuccess)
                .map(state => state.fileName);
              this.fileControl.setValue(successfulUploads.join(', '));
            }

            this.renderer.setStyle(document.body, 'cursor', 'default');
          },
          error: (error) => {
            console.error(`Error deleting ${fileName} from staging:`, error);
            // Still remove from UI even if server delete fails
            this.uploadStates.delete(fileName);
            this.myFiles = this.myFiles.filter((file: any) => file.name !== fileName);

            if (this.uploadStates.size === 0) {
              this.fileControl.setValue('');
            } else {
              const successfulUploads = Array.from(this.uploadStates.values())
                .filter(state => state.uploadSuccess)
                .map(state => state.fileName);
              this.fileControl.setValue(successfulUploads.join(', '));
            }

            this.renderer.setStyle(document.body, 'cursor', 'default');
          }
        });
    } else {
      // File wasn't successfully uploaded yet, just remove from UI
      this.uploadStates.delete(fileName);
      this.myFiles = this.myFiles.filter((file: any) => file.name !== fileName);

      if (this.uploadStates.size === 0) {
        this.fileControl.setValue('');
      } else {
        const successfulUploads = Array.from(this.uploadStates.values())
          .filter(state => state.uploadSuccess)
          .map(state => state.fileName);
        this.fileControl.setValue(successfulUploads.join(', '));
      }
    }
  }
  getUploadStatesArray() {
    return Array.from(this.uploadStates.entries()).map(([key, value]) => ({
      ...value
    }));
  }

  hasSuccessfulUploads(): boolean {
    return Array.from(this.uploadStates.values()).some(state => state.uploadSuccess);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.pythonModuleService.stopConnection();
  }
}
